"use client"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react"

export type GlobalAsideKind = "mobile" | "search" | "cart" | "filter"

export type GlobalAsideContextValue = {
  active: GlobalAsideKind | null
  open(kind: GlobalAsideKind, opener?: HTMLElement | null): void
  close(kind?: GlobalAsideKind): void
  isOpen(kind: GlobalAsideKind): boolean
  restoreFocus(kind: GlobalAsideKind): void
}

const GlobalAsideContext = createContext<GlobalAsideContextValue | null>(null)

export function GlobalAsideProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<GlobalAsideKind | null>(null)
  const activeRef = useRef<GlobalAsideKind | null>(null)
  const openersRef = useRef<Partial<Record<GlobalAsideKind, HTMLElement | null>>>({})
  const restoreTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    activeRef.current = active
  }, [active])

  const restoreFocus = useCallback((kind: GlobalAsideKind) => {
    const opener = openersRef.current[kind]
    openersRef.current[kind] = null
    opener?.focus()
  }, [])

  const open = useCallback((kind: GlobalAsideKind, opener?: HTMLElement | null) => {
    if (restoreTimerRef.current) {
      clearTimeout(restoreTimerRef.current)
      restoreTimerRef.current = null
    }
    const resolvedOpener = opener ?? (
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    )
    openersRef.current[kind] = resolvedOpener
    activeRef.current = kind
    setActive(kind)
  }, [])

  const close = useCallback((kind?: GlobalAsideKind) => {
    const current = activeRef.current
    if (!current || (kind && current !== kind)) return

    activeRef.current = null
    setActive(null)
    if (restoreTimerRef.current) clearTimeout(restoreTimerRef.current)
    restoreTimerRef.current = setTimeout(() => {
      restoreTimerRef.current = null
      restoreFocus(current)
    }, 280)
  }, [restoreFocus])

  useEffect(() => () => {
    if (restoreTimerRef.current) clearTimeout(restoreTimerRef.current)
  }, [])

  const isOpen = useCallback((kind: GlobalAsideKind) => active === kind, [active])

  const value = useMemo<GlobalAsideContextValue>(() => ({
    active,
    open,
    close,
    isOpen,
    restoreFocus,
  }), [active, close, isOpen, open, restoreFocus])

  return (
    <GlobalAsideContext.Provider value={value}>
      {children}
    </GlobalAsideContext.Provider>
  )
}

export function useGlobalAside() {
  const value = useContext(GlobalAsideContext)
  if (!value) throw new Error("useGlobalAside must be used within GlobalAsideProvider")
  return value
}
